import {
  EMPLOYEE_FUNCTION_LABELS,
  EMPLOYEE_FUNCTIONS_BY_CONTRACT_TYPE,
  INVALID_EMAIL_MESSAGE,
} from "@etudescarre/constants";
import {
  Button,
  Checkbox,
  cn,
  Drawer,
  Modal,
  Select,
  type SelectOption,
} from "@etudescarre/ui";
import { isNil, isNotNil, isValidEmail } from "@etudescarre/utils";
import { PlusIcon, ProhibitIcon, TrashIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { cellInputClass } from "@/lib/utils/cell-input";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";
import {
  ContractsPanelFrame,
  DeactivatedLabel,
  notifyDraftSaved,
  type ReferentHistoryEntry,
  refusal,
  warnUnsavedDraft,
} from "./contracts-panel";
import { DeactivateCollaboratorModal } from "./deactivate-collaborator-modal";

export type TeamMember = {
  id: number;
  employeeId: string;
  isActive: boolean;
  isReferent: boolean;
  mail: string | null;
  function: string | null;
  employee: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    companyId: number;
  } | null;
};

type ContractType = keyof typeof EMPLOYEE_FUNCTIONS_BY_CONTRACT_TYPE;

type EmployeeFunction =
  | "directeur_de_travaux"
  | "conduc_de_travaux"
  | "secretariat"
  | "charge_d_affaire"
  | "chef_chantier";

export type Team = {
  id: number;
  name: string;
  delaiPourVisaDays: number | null;
  /** Former team referents, most recently replaced first. */
  referentHistory: ReferentHistoryEntry[];
  members: TeamMember[];
};

export type ContractTeamsDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yardId: number;
  contractId: number;
  contractType: ContractType;
  performerId: number;
  performerName: string;
  teams: Team[];
  /** Prefills the délai pour visa of a new team (909: the contract's délai). */
  contractDelayForNoticeDays?: number | null;
  onSuccess: () => void;
  /**
   * After mise en service, teams and members can no longer be deleted: a
   * member is deactivated on the contract instead (909 and 910).
   */
  isCommissioned?: boolean;
  /** Team names and délais; read-only after mise en service by default (909). */
  teamsEditable?: boolean;
  canAddTeams?: boolean;
};

type TeamDraft = { name: string; delai: string };

// A text field rather than `type="number"`: Safari lets letters into a number
// field and reports it empty, which would save the team without its délai.
const isValidDelai = (value: string) => /^\d*$/.test(value.trim());
const parseDelai = (value: string): number | null =>
  value.trim() === "" ? null : Number(value.trim());

const serverTeamDraft = (team: Team): TeamDraft => ({
  name: team.name,
  delai: isNil(team.delaiPourVisaDays) ? "" : String(team.delaiPourVisaDays),
});

const memberName = (member: TeamMember): string => {
  const employee = member.employee;
  if (isNil(employee)) return member.employeeId;
  return (
    `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim() ||
    member.employeeId
  );
};

export function ContractTeamsDrawer({
  open,
  onOpenChange,
  yardId,
  contractId,
  contractType,
  performerId,
  performerName,
  teams,
  contractDelayForNoticeDays = null,
  onSuccess,
  isCommissioned = false,
  teamsEditable = !isCommissioned,
  canAddTeams = !isCommissioned,
}: ContractTeamsDrawerProps) {
  const editable = teamsEditable;
  const functionOptions: SelectOption[] = EMPLOYEE_FUNCTIONS_BY_CONTRACT_TYPE[
    contractType
  ].map((value) => ({
    value,
    label: EMPLOYEE_FUNCTION_LABELS[value] ?? value,
  }));
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [employeeEmailById, setEmployeeEmailById] = useState<
    Record<string, string | null>
  >({});
  const [busy, setBusy] = useState(false);

  // Create-team form
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamDelai, setTeamDelai] = useState("");

  // Names and délais of saved teams, edited in a draft until "Enregistrer"
  const [teamDrafts, setTeamDrafts] = useState<Map<number, TeamDraft>>(
    new Map(),
  );
  const [isSaving, setIsSaving] = useState(false);

  // Add-member form (per team)
  const [addMemberTeamId, setAddMemberTeamId] = useState<number | null>(null);
  const [memberEmployeeId, setMemberEmployeeId] = useState<string | null>(null);
  const [memberIsReferent, setMemberIsReferent] = useState(false);
  const [memberMail, setMemberMail] = useState("");
  const [memberFunction, setMemberFunction] = useState<EmployeeFunction | null>(
    null,
  );

  // Deactivated on the contract: leaves all its teams, referent roles handed over
  const [deactivating, setDeactivating] = useState<TeamMember | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "team"; id: number; label: string }
    | { kind: "member"; teamId: number; memberId: number; label: string }
    | null
  >(null);

  const fetchEmployees = useCallback(async () => {
    const { data } = await apiClient.GET("/api/admin/employees", {
      params: { query: { companyId: performerId } },
    });
    if (data) {
      // An employee deactivated in their company cannot join a team.
      const activeEmployees = data.data.employees.filter((e) => e.isActive);
      setEmployeeOptions(
        activeEmployees.map((e) => ({
          label:
            `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() ||
            e.email ||
            "Sans nom",
          value: e.id,
        })),
      );
      setEmployeeEmailById(
        Object.fromEntries(activeEmployees.map((e) => [e.id, e.email])),
      );
    }
  }, [performerId]);

  useEffect(() => {
    if (open) {
      fetchEmployees();
      setShowTeamForm(false);
      setTeamName("");
      setTeamDelai("");
      setAddMemberTeamId(null);
      setTeamDrafts(new Map());
    }
  }, [open, fetchEmployees]);

  const getTeamDraft = (team: Team): TeamDraft =>
    teamDrafts.get(team.id) ?? serverTeamDraft(team);

  const onTeamChange = (team: Team, patch: Partial<TeamDraft>) => {
    setTeamDrafts((prev) => {
      const next = {
        ...(prev.get(team.id) ?? serverTeamDraft(team)),
        ...patch,
      };
      const server = serverTeamDraft(team);
      const updated = new Map(prev);
      if (next.name === server.name && next.delai === server.delai) {
        updated.delete(team.id);
      } else {
        updated.set(team.id, next);
      }
      return updated;
    });
  };

  // A deleted team drops out of the draft.
  const dirtyTeams = teams.filter((team) => teamDrafts.has(team.id));
  const isDirty = dirtyTeams.length > 0;

  useUnsavedChangesGuard(isDirty);

  const canSaveTeams = () => {
    if (dirtyTeams.some((team) => getTeamDraft(team).name.trim() === "")) {
      toaster.error({ title: "Erreur", description: "Le nom est requis" });
      return false;
    }
    if (dirtyTeams.some((team) => !isValidDelai(getTeamDraft(team).delai))) {
      toaster.error({ title: "Erreur", description: "Délai invalide" });
      return false;
    }
    return true;
  };

  const saveTeams = async () => {
    setIsSaving(true);
    try {
      const refused: string[] = [];
      for (const team of dirtyTeams) {
        const draft = getTeamDraft(team);
        const { error } = await apiClient.PATCH("/api/admin/teams/{id}", {
          params: { path: { id: team.id } },
          body: {
            name: draft.name.trim(),
            delaiPourVisaDays: parseDelai(draft.delai),
          },
        });
        if (error) refused.push(refusal(`Équipe « ${team.name} »`, error));
      }
      notifyDraftSaved(refused);
      setTeamDrafts(new Map());
      onSuccess();
    } finally {
      setIsSaving(false);
    }
  };

  const openTeamForm = () => {
    setTeamName("");
    setTeamDelai(
      isNil(contractDelayForNoticeDays)
        ? ""
        : String(contractDelayForNoticeDays),
    );
    setShowTeamForm(true);
  };

  const createTeam = async () => {
    if (!teamName.trim()) {
      toaster.error({ title: "Erreur", description: "Le nom est requis" });
      return;
    }
    if (!isValidDelai(teamDelai)) {
      toaster.error({ title: "Erreur", description: "Délai invalide" });
      return;
    }
    setBusy(true);
    try {
      const { error } = await apiClient.POST("/api/admin/teams", {
        body: {
          yardId,
          contractId,
          name: teamName.trim(),
          delaiPourVisaDays: parseDelai(teamDelai) ?? undefined,
        },
      });
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error || "Échec de la création de l'équipe",
        });
        return;
      }
      toaster.success({ title: "Succès", description: "Équipe créée" });
      setShowTeamForm(false);
      setTeamName("");
      setTeamDelai("");
      onSuccess();
    } finally {
      setBusy(false);
    }
  };

  const memberMailInvalid =
    memberMail.trim() !== "" && !isValidEmail(memberMail.trim());

  const addMember = async (teamId: number) => {
    if (!memberEmployeeId) {
      toaster.error({
        title: "Erreur",
        description: "Veuillez sélectionner un collaborateur",
      });
      return;
    }
    setBusy(true);
    const { error } = await apiClient.POST("/api/admin/teams/{id}/members", {
      params: { path: { id: teamId } },
      body: {
        employeeId: memberEmployeeId,
        isReferent: memberIsReferent,
        mail: memberMail.trim() === "" ? null : memberMail.trim(),
        function: memberFunction,
      },
    });
    setBusy(false);
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de l'ajout du membre",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Membre ajouté" });
    setAddMemberTeamId(null);
    setMemberEmployeeId(null);
    setMemberIsReferent(false);
    setMemberMail("");
    setMemberFunction(null);
    onSuccess();
  };

  const setTeamReferent = async (teamId: number, member: TeamMember) => {
    setBusy(true);
    try {
      const { error } = await apiClient.POST("/api/admin/teams/{id}/referent", {
        params: { path: { id: teamId } },
        body: { memberId: member.id },
      });
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error || "Échec de la désignation du référent",
        });
        return;
      }
      toaster.success({
        title: "Succès",
        description: "Référent de l'équipe désigné",
      });
      onSuccess();
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (isNil(deleteTarget)) return;
    setBusy(true);
    let apiError: { error?: string } | undefined;
    if (deleteTarget.kind === "team") {
      ({ error: apiError } = await apiClient.DELETE("/api/admin/teams/{id}", {
        params: { path: { id: deleteTarget.id } },
      }));
    } else {
      ({ error: apiError } = await apiClient.DELETE(
        "/api/admin/teams/{id}/members/{memberId}",
        {
          params: {
            path: { id: deleteTarget.teamId, memberId: deleteTarget.memberId },
          },
        },
      ));
    }
    setBusy(false);
    if (isNotNil(apiError)) {
      toaster.error({
        title: "Erreur",
        description: apiError.error || "Échec de la suppression",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Suppression effectuée" });
    setDeleteTarget(null);
    onSuccess();
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => {
        // The "Enregistrer" button would close with the drawer.
        if (!details.open && isDirty) {
          warnUnsavedDraft(
            "Enregistrez ou annulez les modifications des équipes avant de fermer ce panneau.",
          );
          return;
        }
        onOpenChange(details.open);
      }}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          {/* Medium width: the draft's Enregistrer/Annuler bar must fit. */}
          <Drawer.Content className="!w-[var(--width-side-tray-medium)]">
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>Équipes</Drawer.Title>
                <Drawer.Description>{performerName}</Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <div className="min-h-0 flex-1 px-6 pt-3 pb-6">
              <ContractsPanelFrame
                // The draft's bar takes the toolbar's place: save or cancel
                // the edits before adding a team.
                toolbar={
                  canAddTeams &&
                  !isDirty && (
                    <Button
                      outline
                      onClick={openTeamForm}
                      disabled={showTeamForm}
                    >
                      <PlusIcon data-slot="icon" className="size-4" />
                      Ajouter une équipe
                    </Button>
                  )
                }
                isDirty={isDirty}
                isSaving={isSaving}
                canSave={canSaveTeams}
                saveMessage="Les modifications apportées aux équipes vont être enregistrées."
                onSave={saveTeams}
                onDiscard={() => setTeamDrafts(new Map())}
              >
                <div className="space-y-4">
                  {canAddTeams && showTeamForm && (
                    <div className="space-y-2 rounded-lg border border-zinc-200 p-3 dark:border-zinc-800">
                      <label className="block space-y-1 text-xs text-zinc-500">
                        <span>Nom de l'équipe</span>
                        <input
                          type="text"
                          value={teamName}
                          onChange={(e) => setTeamName(e.target.value)}
                          placeholder="Nom de l'équipe"
                          maxLength={255}
                          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                        />
                      </label>
                      <label className="block space-y-1 text-xs text-zinc-500">
                        <span>Délai pour visa (jours, optionnel)</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={teamDelai}
                          onChange={(e) => setTeamDelai(e.target.value)}
                          placeholder="Délai pour visa (jours, optionnel)"
                          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                        />
                      </label>
                      <div className="flex justify-end gap-2">
                        <Button
                          plain
                          onClick={() => setShowTeamForm(false)}
                          disabled={busy}
                        >
                          Annuler
                        </Button>
                        <Button
                          onClick={createTeam}
                          disabled={busy || !teamName}
                        >
                          Créer
                        </Button>
                      </div>
                    </div>
                  )}

                  {teams.length === 0 && !showTeamForm && (
                    <p className="text-sm text-zinc-400">
                      Aucune équipe pour ce contrat
                    </p>
                  )}

                  {teams.map((team) => {
                    const draft = getTeamDraft(team);
                    const dirty = teamDrafts.has(team.id);
                    // A member deactivated on the contract comes back by being
                    // added again: only active members are left out.
                    const addableOptions = employeeOptions.filter(
                      (option) =>
                        !team.members.some(
                          (m) => m.isActive && m.employeeId === option.value,
                        ),
                    );
                    return (
                      <div
                        key={team.id}
                        className="rounded-lg border border-zinc-200 dark:border-zinc-800"
                      >
                        <div
                          className={cn(
                            "flex items-center justify-between gap-2 border-b border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900",
                            dirty && "bg-amber-50 dark:bg-amber-900/20",
                          )}
                        >
                          {editable ? (
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <input
                                type="text"
                                aria-label="Nom de l'équipe"
                                title={draft.name}
                                value={draft.name}
                                onChange={(e) =>
                                  onTeamChange(team, { name: e.target.value })
                                }
                                maxLength={255}
                                className={cn(
                                  cellInputClass(dirty),
                                  "min-w-0 flex-1 font-medium",
                                )}
                              />
                              <label className="flex shrink-0 items-center gap-1 text-xs text-zinc-500">
                                Délai visa (j)
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  aria-label="Délai pour visa (jours)"
                                  value={draft.delai}
                                  onChange={(e) =>
                                    onTeamChange(team, {
                                      delai: e.target.value,
                                    })
                                  }
                                  className={cn(cellInputClass(dirty), "w-14")}
                                />
                              </label>
                            </div>
                          ) : (
                            <span className="min-w-0 truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                              {team.name}
                              {isNotNil(team.delaiPourVisaDays) && (
                                <span className="ml-2 text-xs text-zinc-400">
                                  (visa {team.delaiPourVisaDays} j)
                                </span>
                              )}
                            </span>
                          )}
                          {!isCommissioned && (
                            <Button
                              plain
                              onClick={() =>
                                setDeleteTarget({
                                  kind: "team",
                                  id: team.id,
                                  label: `l'équipe ${team.name}`,
                                })
                              }
                              aria-label="Supprimer l'équipe"
                            >
                              <TrashIcon className="size-4" />
                            </Button>
                          )}
                        </div>

                        <div className="space-y-1 p-3">
                          {team.members.length === 0 ? (
                            <p className="text-xs text-zinc-400">
                              Aucun membre
                            </p>
                          ) : (
                            team.members.map((member) => (
                              <div
                                key={member.id}
                                className="flex items-center justify-between py-1 text-sm"
                              >
                                <span
                                  className={
                                    member.isActive
                                      ? "text-zinc-700 dark:text-zinc-300"
                                      : "text-zinc-400"
                                  }
                                >
                                  {memberName(member)}
                                  {member.isReferent && (
                                    <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                      Référent
                                    </span>
                                  )}
                                  {isNotNil(member.function) && (
                                    <span className="ml-2 text-xs text-zinc-400">
                                      {EMPLOYEE_FUNCTION_LABELS[
                                        member.function
                                      ] ?? member.function}
                                    </span>
                                  )}
                                </span>
                                <div className="flex shrink-0 items-center gap-1">
                                  {member.isActive && !member.isReferent && (
                                    // A saved member can become the referent.
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setTeamReferent(team.id, member)
                                      }
                                      disabled={busy}
                                      className="text-xs font-medium text-blue-600 hover:underline disabled:opacity-50 dark:text-blue-400"
                                    >
                                      Définir référent
                                    </button>
                                  )}
                                  {!member.isActive && <DeactivatedLabel />}
                                  {isCommissioned ? (
                                    member.isActive && (
                                      <Button
                                        plain
                                        onClick={() => setDeactivating(member)}
                                        disabled={busy}
                                        aria-label="Désactiver le membre"
                                        title="Désactiver sur ce contrat : il sort de toutes ses équipes"
                                      >
                                        <ProhibitIcon className="size-3.5" />
                                      </Button>
                                    )
                                  ) : (
                                    <Button
                                      plain
                                      onClick={() =>
                                        setDeleteTarget({
                                          kind: "member",
                                          teamId: team.id,
                                          memberId: member.id,
                                          label: `le membre ${memberName(member)}`,
                                        })
                                      }
                                      aria-label="Retirer le membre"
                                    >
                                      <TrashIcon className="size-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))
                          )}

                          {!canAddTeams ? null : addMemberTeamId === team.id ? (
                            <div className="mt-2 space-y-2 rounded-md border border-zinc-200 p-2 dark:border-zinc-800">
                              <Select
                                options={addableOptions}
                                value={
                                  memberEmployeeId ? [memberEmployeeId] : []
                                }
                                onValueChange={(details) => {
                                  const nextEmployeeId =
                                    details.value[0] ?? null;
                                  setMemberEmployeeId(nextEmployeeId);
                                  setMemberMail(
                                    nextEmployeeId
                                      ? (employeeEmailById[nextEmployeeId] ??
                                          "")
                                      : "",
                                  );
                                }}
                                placeholder={
                                  addableOptions.length > 0
                                    ? "Sélectionner un collaborateur"
                                    : employeeOptions.length > 0
                                      ? "Tous les collaborateurs sont déjà membres"
                                      : "Aucun collaborateur pour cette société"
                                }
                                disabled={addableOptions.length === 0}
                              />
                              {addableOptions.length <
                                employeeOptions.length && (
                                <p className="text-xs text-zinc-500">
                                  Les membres déjà dans cette équipe ne sont pas
                                  proposés.
                                </p>
                              )}
                              {memberEmployeeId && (
                                <input
                                  type="email"
                                  value={memberMail}
                                  onChange={(e) =>
                                    setMemberMail(e.target.value)
                                  }
                                  placeholder="Mail de correspondance"
                                  aria-invalid={memberMailInvalid}
                                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                                />
                              )}
                              {memberMailInvalid && (
                                <p className="text-red-600 text-sm">
                                  {INVALID_EMAIL_MESSAGE}
                                </p>
                              )}
                              <Select
                                options={functionOptions}
                                value={memberFunction ? [memberFunction] : []}
                                onValueChange={(details) =>
                                  setMemberFunction(
                                    (details.value[0] as EmployeeFunction) ??
                                      null,
                                  )
                                }
                                placeholder="Fonction (optionnel)"
                              />
                              <div className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                                <Checkbox
                                  checked={memberIsReferent}
                                  onCheckedChange={(details) =>
                                    setMemberIsReferent(
                                      details.checked === true,
                                    )
                                  }
                                />
                                <span>Référent de l'équipe</span>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  plain
                                  onClick={() => {
                                    setAddMemberTeamId(null);
                                    setMemberEmployeeId(null);
                                    setMemberIsReferent(false);
                                    setMemberMail("");
                                    setMemberFunction(null);
                                  }}
                                  disabled={busy}
                                >
                                  Annuler
                                </Button>
                                <Button
                                  onClick={() => addMember(team.id)}
                                  disabled={
                                    busy ||
                                    !memberEmployeeId ||
                                    memberMailInvalid
                                  }
                                >
                                  Ajouter
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setAddMemberTeamId(team.id);
                                setMemberEmployeeId(null);
                                setMemberIsReferent(false);
                                setMemberMail("");
                                setMemberFunction(null);
                              }}
                              className="mt-1 flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                            >
                              <PlusIcon className="size-3.5" />
                              Ajouter un membre
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ContractsPanelFrame>
            </div>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>

      {isNotNil(deactivating) && (
        <DeactivateCollaboratorModal
          open
          onOpenChange={(open) => {
            if (!open) setDeactivating(null);
          }}
          employee={{
            id: deactivating.employeeId,
            name: memberName(deactivating),
          }}
          contractId={contractId}
          onSuccess={onSuccess}
        />
      )}

      <Modal.Root
        open={isNotNil(deleteTarget)}
        onOpenChange={(details) => {
          if (!details.open) setDeleteTarget(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Voulez-vous vraiment supprimer{" "}
                  <span className="font-medium">{deleteTarget?.label}</span> ?
                  Cette action est irréversible.
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteTarget(null)}
                  disabled={busy}
                >
                  Annuler
                </Button>
                <Button color="red" onClick={confirmDelete} disabled={busy}>
                  {busy ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </Drawer.Root>
  );
}
